import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import ViewConfigurationUpdateAuditLogDetailsModal from "./ViewConfigurationUpdateAuditLogDetailsModal";
import ViewExportEntitiesAuditLogDetailsModal from "./ViewExportEntitiesAuditLogDetailsModal";
import {AuditLogQueryParams, AuditLogsRepository,} from "../../repositories/AuditLogsRepository";
import {AuditLog, AuditLogPerformerTypeColors, AuditLogPerformerTypeIcons, AuditLogPerformerTypeLabels, AuditLogType, getAuditLogTypeLabel,} from "../../models/AuditLog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { tableWrapperClassName } from "../common/Table/TableStyles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ViewExportFilesAuditLogDetailsModal from "./ViewExportFilesAuditLogDetailsModal";
import ViewOrganizationAssignedUserChangeAuditLogDetailsModal from "./ViewOrganizationAssignedUserChangeAuditLogDetailsModal";
import ViewOrganizationTemporaryAccessGrantAuditLogDetailsModal from "./ViewOrganizationTemporaryAccessGrantAuditLogDetailsModal";
import ViewClientConfigurationChangeAuditLogDetailsModal from "./ViewClientConfigurationChangeAuditLogDetailsModal";
import { ViewDeleteAuditLogDetailsModal } from "./ViewDeleteAuditLogDetailsModal";
import ViewFilesPriorityRecalculationAuditLogDetailsModal from "./ViewFilesPriorityRecalculationAuditLogDetailsModal";
import ViewSourceLinksExportAuditLogDetailsModal from "./ViewSourceLinksExportAuditLogDetailsModal";
import {convertUtcToLocal} from "../../helpers/CommonHelpers";
import ViewMarkNotFoundFileForRetryAuditLogDetailsModal from "./ViewMarkNotFoundFileForRetryAuditLogDetailsModal";
import {ViewClientSetToPendingUninstallDetailsModal} from "./ViewClientSetToPendingUninstallDetailsModal";
import {ViewFlagUpdateDetailsModal } from "./ViewFlagUpdateDetailsModal";
import ViewClientCacheSettingsChangeAuditLogDetailsModal from "./ViewClientCacheSettingsChangeAuditLogDetailsModal";
import {ViewSqlScriptExecuteAuditLogDetailsModal } from "./ViewSqlScriptExecuteAuditLogDetailsModal";
import ViewResolveConflictAuditLogDetailsModal from "./ViewResolveConflictAuditLogDetailsModal";
import ViewOrgWideConflictResolutionAuditLogDetailsModal from "./ViewOrgWideConflictResolutionAuditLogDetailsModal";
import ViewPathCollisionReconcileAuditLogDetailsModal from "./ViewPathCollisionReconcileAuditLogDetailsModal";
import ViewConflictRuleRunAuditLogDetailsModal from "./ViewConflictRuleRunAuditLogDetailsModal";
import ViewConflictRuleRunExportAuditLogDetailsModal from "./ViewConflictRuleRunExportAuditLogDetailsModal";
import ViewGraphLibraryVersioningChangeAuditLogDetailsModal from "./ViewGraphLibraryVersioningChangeAuditLogDetailsModal";
import ViewBulkDeletionAuditLogDetailsModal from "./ViewBulkDeletionAuditLogDetailsModal";
import ViewDeletionRequestedAuditLogDetailsModal from "./ViewDeletionRequestedAuditLogDetailsModal";
import {useTableHandlers} from "../common/Table/Hooks/UseTableHandlers";
import TablePagination from "../common/Table/TablePagination";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import TableEmptyState from "../common/TableEmptyState";
import { ViewFolderSweepRequestedAuditLogDetailsModal } from "./ViewFolderSweepRequestedAuditLogDetailsModal";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AUDIT_LOG_MODAL_REGISTRY: Record<string, React.FC<any>> = {
  // Configuration changes
  [AuditLogType.OrganizationConfigurationChange]: ViewConfigurationUpdateAuditLogDetailsModal,
  [AuditLogType.ClientConfigurationChange]: ViewConfigurationUpdateAuditLogDetailsModal,
  [AuditLogType.ClientConfigurationCreate]: ViewConfigurationUpdateAuditLogDetailsModal,
  [AuditLogType.ClientConfigurationDelete]: ViewConfigurationUpdateAuditLogDetailsModal,
  [AuditLogType.FlaggedPathChange]: ViewConfigurationUpdateAuditLogDetailsModal,
  // Entity exports
  [AuditLogType.CasesExport]: ViewExportEntitiesAuditLogDetailsModal,
  [AuditLogType.FirmDocumentsFoldersExport]: ViewExportEntitiesAuditLogDetailsModal,
  [AuditLogType.ProvidersExport]: ViewExportEntitiesAuditLogDetailsModal,
  [AuditLogType.TemplatesExport]: ViewExportEntitiesAuditLogDetailsModal,
  [AuditLogType.IntakesExport]: ViewExportEntitiesAuditLogDetailsModal,
  [AuditLogType.FlaggedPathsExport]: ViewExportEntitiesAuditLogDetailsModal,
  // File exports
  [AuditLogType.CaseFilesExport]: ViewExportFilesAuditLogDetailsModal,
  [AuditLogType.FirmDocumentsExport]: ViewExportFilesAuditLogDetailsModal,
  [AuditLogType.ProviderFilesExport]: ViewExportFilesAuditLogDetailsModal,
  [AuditLogType.TemplateFilesExport]: ViewExportFilesAuditLogDetailsModal,
  [AuditLogType.IntakeFilesExport]: ViewExportFilesAuditLogDetailsModal,
  // Organization management
  [AuditLogType.OrganizationAssignedUserChange]: ViewOrganizationAssignedUserChangeAuditLogDetailsModal,
  [AuditLogType.OrganizationTemporaryAccessGrant]: ViewOrganizationTemporaryAccessGrantAuditLogDetailsModal,
  // Client configuration changes
  [AuditLogType.DefaultClientConfigurationChange]: ViewClientConfigurationChangeAuditLogDetailsModal,
  [AuditLogType.ActiveClientConfigurationChange]: ViewClientConfigurationChangeAuditLogDetailsModal,
  // Mark for retry
  [AuditLogType.MarkNotFoundFilesForRetry]: ViewMarkNotFoundFileForRetryAuditLogDetailsModal,
  [AuditLogType.MarkNotFoundCaseFileForRetry]: ViewMarkNotFoundFileForRetryAuditLogDetailsModal,
  [AuditLogType.MarkNotFoundIntakeFileForRetry]: ViewMarkNotFoundFileForRetryAuditLogDetailsModal,
  [AuditLogType.MarkNotFoundProviderFileForRetry]: ViewMarkNotFoundFileForRetryAuditLogDetailsModal,
  [AuditLogType.MarkNotFoundTemplateFileForRetry]: ViewMarkNotFoundFileForRetryAuditLogDetailsModal,
  [AuditLogType.MarkNotFoundFirmDocumentForRetry]: ViewMarkNotFoundFileForRetryAuditLogDetailsModal,
  // Client actions
  [AuditLogType.ClientSetToPendingUninstall]: ViewClientSetToPendingUninstallDetailsModal,
  [AuditLogType.ClientForceRemoved]: ViewClientSetToPendingUninstallDetailsModal,
  [AuditLogType.ClientSoftDeleted]: ViewClientSetToPendingUninstallDetailsModal,
  [AuditLogType.ClientUninstallCancelled]: ViewClientSetToPendingUninstallDetailsModal,
  [AuditLogType.ClientVersionBlacklistReset]: ViewClientSetToPendingUninstallDetailsModal,
  // Flag/block updates
  [AuditLogType.CaseFlagUpdate]: ViewFlagUpdateDetailsModal,
  [AuditLogType.CaseBlockUpdate]: ViewFlagUpdateDetailsModal,
  [AuditLogType.IntakeFlagUpdate]: ViewFlagUpdateDetailsModal,
  [AuditLogType.IntakeBlockUpdate]: ViewFlagUpdateDetailsModal,
  [AuditLogType.ProviderBlockUpdate]: ViewFlagUpdateDetailsModal,
  [AuditLogType.TemplatesLibraryBlockUpdate]: ViewFlagUpdateDetailsModal,
  [AuditLogType.FirmDocumentsFolderBlockUpdate]: ViewFlagUpdateDetailsModal,
  // Conflict resolutions
  [AuditLogType.CaseFileConflictResolved]: ViewResolveConflictAuditLogDetailsModal,
  [AuditLogType.IntakeFileConflictResolved]: ViewResolveConflictAuditLogDetailsModal,
  [AuditLogType.ProviderFileConflictResolved]: ViewResolveConflictAuditLogDetailsModal,
  [AuditLogType.TemplateFileConflictResolved]: ViewResolveConflictAuditLogDetailsModal,
  [AuditLogType.FirmDocumentConflictResolved]: ViewResolveConflictAuditLogDetailsModal,
  // One org-wide bulk resolution covers many files, so it gets a summary modal with an on-demand file
  // drill-down rather than the per-file view the five types above use.
  [AuditLogType.OrgWideConflictResolution]: ViewOrgWideConflictResolutionAuditLogDetailsModal,
  [AuditLogType.PathCollisionFlagsReconciled]: ViewPathCollisionReconcileAuditLogDetailsModal,
  // One rule run decides many groups, so, like the org-wide resolution, it gets a summary with an on-demand drill-down.
  [AuditLogType.ConflictRuleRun]: ViewConflictRuleRunAuditLogDetailsModal,
  [AuditLogType.ConflictRuleRunExport]: ViewConflictRuleRunExportAuditLogDetailsModal,
  // Other
  [AuditLogType.ClientCacheSettingsChange]: ViewClientCacheSettingsChangeAuditLogDetailsModal,
  [AuditLogType.SqlScriptExecute]: ViewSqlScriptExecuteAuditLogDetailsModal,
  [AuditLogType.BulkDeletionByUploadedIds]: ViewBulkDeletionAuditLogDetailsModal,
  [AuditLogType.DeletionRequested]: ViewDeletionRequestedAuditLogDetailsModal,
  [AuditLogType.GraphLibraryVersioningChange]: ViewGraphLibraryVersioningChangeAuditLogDetailsModal,
  [AuditLogType.FolderSweepRequested]: ViewFolderSweepRequestedAuditLogDetailsModal,
  [AuditLogType.RefreshTokenDeleted]: ViewDeleteAuditLogDetailsModal,
  [AuditLogType.OrganizationDeleted]: ViewDeleteAuditLogDetailsModal,
  [AuditLogType.ConsentLinkCreated]: ViewDeleteAuditLogDetailsModal,
  [AuditLogType.FilesPriorityRecalculation]: ViewFilesPriorityRecalculationAuditLogDetailsModal,
  [AuditLogType.SourceLinksExport]: ViewSourceLinksExportAuditLogDetailsModal,
};

interface AuditLogTableProps {
  organizationId: string;
  queryParams: AuditLogQueryParams;
  shouldFetchAuditLogs: boolean;
  /** Passed to every details modal; a conflict rule run's uses it to offer Load rule and its CSV. */
  canUseConflictRules?: boolean;
  /** An entry to open the details of once the first page loads, if it is on it. */
  openAuditLogId?: string;
}

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  organizationId,
  queryParams,
  shouldFetchAuditLogs,
  canUseConflictRules = false,
  openAuditLogId,
}) => {
  const auditLogsRepository = new AuditLogsRepository();
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [viewAuditLogDetailsOpened, setViewAuditLogDetailsOpened] = useState<boolean>(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState<AuditLog | null>(null);
  // Only the first page that loads is searched, so a later refetch never reopens details someone closed.
  const openAuditLogHandled = useRef(!openAuditLogId);

  const {
    order,
    sort,
    page,
    rowsPerPage,
    rows,
    setRows,
    setPage,
    changeSort,
    changeOrder,
    handleRowsPerPageChange
  } = useTableHandlers({
    order: "desc",
    sort: "dateCreated",
    page: 0,
    rowsPerPage: 10
  });

  const auditLogsQuery = useQuery({ queryKey: [QUERY_KEYS.AUDIT_LOGS, organizationId, page, rowsPerPage, sort, order, queryParams], queryFn:
    async () => {
      return await auditLogsRepository.getAuditLogs({
        params: queryParams,
        descending: order === "desc",
        orderBy: sort,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      });
    }, enabled: shouldFetchAuditLogs,
      staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (auditLogsQuery.data?.data) {
      setAuditLogs(auditLogsQuery.data.data);
      setRows(auditLogsQuery.data.paging?.totalCount ?? 0);
      if (!openAuditLogHandled.current) {
        openAuditLogHandled.current = true;
        const toOpen = auditLogsQuery.data.data.find((auditLog) => auditLog.id === openAuditLogId);
        if (toOpen && AUDIT_LOG_MODAL_REGISTRY[toOpen.eventType]) handleOpenModal(toOpen);
      }
    }
  }, [auditLogsQuery]);

  const handleOpenModal = (auditLog: AuditLog) => {
    setViewAuditLogDetailsOpened(true);
    setSelectedAuditLog(auditLog);
  };

  const renderAuditLogDetailsModal = () => {
    if (!selectedAuditLog) return null;
    const ModalComponent = AUDIT_LOG_MODAL_REGISTRY[selectedAuditLog.eventType];
    if (!ModalComponent) return null;
    return (
      <ModalComponent
        auditLog={selectedAuditLog}
        open={viewAuditLogDetailsOpened}
        onClose={() => setViewAuditLogDetailsOpened(false)}
        organizationId={organizationId}
        canUseConflictRules={canUseConflictRules}
      />
    );
  };

  return (
    <>
      <div className={cn(tableWrapperClassName, (auditLogsQuery.isFetching || auditLogsQuery.isError) && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[35%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Event Type"}
                  sortProperty={"eventType"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[25%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Event Date"}
                  sortProperty={"dateCreated"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[25%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Performed By"}
                  sortProperty={"performedBy"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[25%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Performer Type"}
                  sortProperty={"performerType"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[15%] px-1.5 py-3"></TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {auditLogs.map((auditLog) => (
              <TableRow key={auditLog.id}>
                <TableCell>
                  <Badge variant="neutral">{getAuditLogTypeLabel(auditLog.eventType)}</Badge>
                </TableCell>
                <TableCell>{convertUtcToLocal(auditLog.eventDate)}</TableCell>
                <TableCell>{auditLog.performerEmailAddress || auditLog.performedBy}</TableCell>
                <TableCell>
                  {(() => {
                    const Icon = AuditLogPerformerTypeIcons[auditLog.performerType];
                    return (
                      <Badge variant={AuditLogPerformerTypeColors[auditLog.performerType]}>
                        <Icon className="size-3" />
                        {AuditLogPerformerTypeLabels[auditLog.performerType]}
                      </Badge>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {AUDIT_LOG_MODAL_REGISTRY[auditLog.eventType] && (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-sm"
                      onClick={() => handleOpenModal(auditLog)}
                    >
                      View Details
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
              {auditLogs.length === 0 && !auditLogsQuery.isFetching && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No audit logs found" />
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>

      {renderAuditLogDetailsModal()}
    </>
  );
};

export default AuditLogTable;
